import posthog from 'posthog-js';
import type { ErrorInfo } from 'react';

/**
 * Get common error context
 */
function getErrorContext() {
  return {
    // Location Context
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
  };
}

/**
 * Captures runtime/component errors in PostHog with full context
 */
export function captureRuntimeErrorInPostHog(
  error: Error,
  errorInfo?: ErrorInfo
): void {
  // Parse component stack to get the component that failed
  const componentStack = errorInfo?.componentStack || '';
  const firstComponent = componentStack
    .split('\n')
    .find((line) => line.trim().startsWith('at'))
    ?.trim()
    .replace('at ', '');

  // Capture error in PostHog with rich context
  posthog.captureException(error, {
    // Error Details
    error_type: 'runtime_error',
    error_name: error.name,
    error_message: error.message,
    error_stack: error.stack,

    // React Component Context
    component_stack: componentStack,
    failed_component: firstComponent || 'Unknown Component',

    // Common Context
    ...getErrorContext(),
  });
}

/**
 * Captures unhandled promise rejections in PostHog
 */
export function captureUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;

  // Convert reason to Error if it's not already
  let error: Error;
  if (reason instanceof Error) {
    error = reason;
  } else if (typeof reason === 'string') {
    error = new Error(reason);
  } else {
    error = new Error(JSON.stringify(reason));
  }

  const errorTitle = `UnhandledRejection: ${error.message}`;

  // Capture error in PostHog
  posthog.captureException(new Error(errorTitle), {
    // Error Details
    error_type: 'unhandled_rejection',
    error_name: error.name,
    error_message: error.message,
    error_stack: error.stack,
    rejection_reason: String(reason),

    // Common Context
    ...getErrorContext(),
  });
}

/**
 * Captures global errors in PostHog
 */
export function captureGlobalError(event: ErrorEvent): void {
  const error = event.error || new Error(event.message);

  // Capture error in PostHog
  posthog.captureException(error, {
    // Error Details
    error_type: 'global_error',
    error_name: error.name,
    error_message: error.message,
    error_stack: error.stack,
    error_filename: event.filename,
    error_lineno: event.lineno,
    error_colno: event.colno,

    // Common Context
    ...getErrorContext(),
  });
}

/**
 * Setup global error handlers for PostHog
 * Call this once in your app's main entry point
 */
export function setupGlobalErrorHandlers(): void {
  window.addEventListener('unhandledrejection', captureUnhandledRejection);
  window.addEventListener('error', captureGlobalError);
}

/**
 * Cleanup global error handlers
 * Call this when unmounting/cleaning up
 */
export function cleanupGlobalErrorHandlers(): void {
  window.removeEventListener('unhandledrejection', captureUnhandledRejection);
  window.removeEventListener('error', captureGlobalError);
}
